import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, parseErrorDetail } from "../lib/api";

const CENTS = (c) =>
  typeof c === "number" && !Number.isNaN(c)
    ? (c / 100).toLocaleString(undefined, { style: "currency", currency: "AUD" })
    : "—";

const CATEGORIES = [
  { value: "CORE", label: "Core" },
  { value: "PREMIUM_SPECIAL", label: "Premium/Special" },
];

const EMPTY_FORM = {
  product_id: "",
  display_name: "",
  type: "",
  specifications: "",
  category: "CORE",
  pack_size: "20L",
  display_order: 0,
  standard_dollars: "",
  ten_plus_dollars: "",
  forty_eight_plus_dollars: "",
  mixed_twenty_dollars: "",
};

const toCents = (s) => {
  if (s === "" || s === null || s === undefined) return null;
  const n = Number(String(s).replace(/[^0-9.\-]/g, ""));
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100);
};

function ProductModal({ mode, initial, onClose, onDone }) {
  const [f, setF] = useState(() => ({ ...EMPTY_FORM, ...(initial || {}) }));
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isEdit = mode === "edit";
  const isCore = f.category === "CORE";

  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    // Client-side sanity — server is authoritative.
    if (!isEdit && !/^[A-Z0-9\-]{2,64}$/.test(f.product_id.trim().toUpperCase())) {
      setError("SKU must be 2–64 characters, upper-case alphanumerics and hyphen only.");
      return;
    }
    const std = toCents(f.standard_dollars);
    if (std === null || std <= 0) {
      setError("Standard price must be a positive amount in dollars.");
      return;
    }
    let ten = toCents(f.ten_plus_dollars);
    let forty = toCents(f.forty_eight_plus_dollars);
    let mixed = toCents(f.mixed_twenty_dollars);
    if (isCore) {
      if (!ten || !forty) { setError("CORE products require 10+ Support and 48+ Best Price."); return; }
      mixed = null;
    } else {
      if (!mixed) { setError("PREMIUM/SPECIAL products require Mixed 20+."); return; }
      ten = null; forty = null;
    }
    setSubmitting(true);
    try {
      if (isEdit) {
        const payload = {
          display_name: f.display_name,
          type: f.type,
          specifications: f.specifications,
          pack_size: f.pack_size,
          display_order: Number(f.display_order),
          standard_price_cents: std,
          ten_plus_price_cents: ten,
          forty_eight_plus_price_cents: forty,
          mixed_twenty_price_cents: mixed,
        };
        // Strip nulls except when server explicitly supports None. Server
        // treats absent as "no change"; nulls confuse it here.
        Object.keys(payload).forEach((k) => (payload[k] === null || payload[k] === undefined) && delete payload[k]);
        await api.patch(`/admin/catalogue/products/${encodeURIComponent(f.product_id)}`, payload);
      } else {
        await api.post("/admin/catalogue/products", {
          product_id: f.product_id.trim().toUpperCase(),
          display_name: f.display_name.trim(),
          type: f.type.trim(),
          specifications: f.specifications.trim(),
          category: f.category,
          pack_size: f.pack_size.trim(),
          display_order: Number(f.display_order) || 0,
          standard_price_cents: std,
          ten_plus_price_cents: ten,
          forty_eight_plus_price_cents: forty,
          mixed_twenty_price_cents: mixed,
        });
      }
      onDone();
    } catch (err) {
      setError(parseErrorDetail(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="apwo-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="cat-modal-title" data-testid="cat-modal">
      <div className="apwo-modal-card apwo-cat-modal-card">
        <header className="apwo-modal-head">
          <h3 id="cat-modal-title" className="apwo-modal-title">
            {isEdit ? `Edit product · ${f.product_id}` : "Add product"}
          </h3>
          <button type="button" className="apwo-modal-close" onClick={onClose} aria-label="Close" data-testid="cat-modal-close">×</button>
        </header>
        <div className="apwo-modal-body apwo-cat-modal-body">
          <form onSubmit={submit} noValidate autoComplete="off" data-testid="cat-form">
            <div className="apwo-grid-two">
              <label className="apwo-field">
                <span>SKU</span>
                <input type="text" value={f.product_id} onChange={(e) => set("product_id", e.target.value)}
                       required disabled={isEdit} data-testid="cat-sku" />
              </label>
              <label className="apwo-field">
                <span>Category</span>
                <select value={f.category} onChange={(e) => set("category", e.target.value)}
                        disabled={isEdit} data-testid="cat-category">
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </label>
            </div>
            <label className="apwo-field">
              <span>Display name</span>
              <input type="text" value={f.display_name} onChange={(e) => set("display_name", e.target.value)} required data-testid="cat-name" />
            </label>
            <div className="apwo-grid-two">
              <label className="apwo-field">
                <span>Type</span>
                <input type="text" value={f.type} onChange={(e) => set("type", e.target.value)} required data-testid="cat-type" />
              </label>
              <label className="apwo-field">
                <span>Pack size</span>
                <input type="text" value={f.pack_size} onChange={(e) => set("pack_size", e.target.value)} required data-testid="cat-pack" />
              </label>
            </div>
            <label className="apwo-field">
              <span>Specifications</span>
              <input type="text" value={f.specifications} onChange={(e) => set("specifications", e.target.value)} required data-testid="cat-specs" />
            </label>
            <div className="apwo-grid-two">
              <label className="apwo-field">
                <span>Display order</span>
                <input type="number" min="0" step="1" value={f.display_order} onChange={(e) => set("display_order", e.target.value)} data-testid="cat-order" />
              </label>
              <label className="apwo-field">
                <span>Standard price (AUD)</span>
                <input type="number" min="0" step="0.01" value={f.standard_dollars} onChange={(e) => set("standard_dollars", e.target.value)} required data-testid="cat-standard" />
              </label>
            </div>
            {isCore ? (
              <div className="apwo-grid-two">
                <label className="apwo-field">
                  <span>10+ Support price (AUD)</span>
                  <input type="number" min="0" step="0.01" value={f.ten_plus_dollars} onChange={(e) => set("ten_plus_dollars", e.target.value)} data-testid="cat-tenplus" />
                </label>
                <label className="apwo-field">
                  <span>48+ Best Price (AUD)</span>
                  <input type="number" min="0" step="0.01" value={f.forty_eight_plus_dollars} onChange={(e) => set("forty_eight_plus_dollars", e.target.value)} data-testid="cat-fortyplus" />
                </label>
              </div>
            ) : (
              <label className="apwo-field">
                <span>Mixed 20+ price (AUD)</span>
                <input type="number" min="0" step="0.01" value={f.mixed_twenty_dollars} onChange={(e) => set("mixed_twenty_dollars", e.target.value)} data-testid="cat-mixedtwenty" />
              </label>
            )}
            {error && <div className="apwo-alert-error" role="alert" data-testid="cat-form-error">{error}</div>}
            <div className="apwo-modal-actions">
              <button type="button" className="apwo-btn-secondary" onClick={onClose} disabled={submitting} data-testid="cat-cancel">Cancel</button>
              <button type="submit" className="apwo-btn-primary" disabled={submitting} data-testid="cat-submit">
                {submitting ? "Saving…" : isEdit ? "Save changes" : "Add product"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function AdminCataloguePage() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [products, setProducts] = useState(null);
  const [error, setError] = useState("");
  const [modalMode, setModalMode] = useState(null); // "add" | "edit" | null
  const [modalInitial, setModalInitial] = useState(null);
  const [banner, setBanner] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/catalogue/products");
      setProducts(r.data);
    } catch (err) {
      setError(parseErrorDetail(err));
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (p) => {
    try {
      await api.post(`/admin/catalogue/products/${encodeURIComponent(p.product_id)}/stock`,
                     { in_stock: !p.in_stock });
      setBanner({ kind: "success", text: `${p.product_id} → ${!p.in_stock ? "In stock" : "Out of stock"}.` });
      await load();
      setTimeout(() => setBanner(null), 5000);
    } catch (err) {
      setError(parseErrorDetail(err));
    }
  };

  const openEdit = (p) => {
    setModalInitial({
      product_id: p.product_id,
      display_name: p.display_name,
      type: p.type,
      specifications: p.specifications,
      category: p.category,
      pack_size: p.pack_size,
      display_order: p.display_order,
      standard_dollars: ((p.prices_cents?.standard || 0) / 100).toFixed(2),
      ten_plus_dollars: p.prices_cents?.ten_plus != null ? (p.prices_cents.ten_plus / 100).toFixed(2) : "",
      forty_eight_plus_dollars: p.prices_cents?.forty_eight_plus != null ? (p.prices_cents.forty_eight_plus / 100).toFixed(2) : "",
      mixed_twenty_dollars: p.prices_cents?.mixed_twenty != null ? (p.prices_cents.mixed_twenty / 100).toFixed(2) : "",
    });
    setModalMode("edit");
  };

  const openAdd = () => {
    setModalInitial(null);
    setModalMode("add");
  };

  const onModalDone = async () => {
    setModalMode(null);
    setModalInitial(null);
    setBanner({ kind: "success", text: "Catalogue updated." });
    await load();
    setTimeout(() => setBanner(null), 4000);
  };

  const grouped = useMemo(() => {
    if (!products) return null;
    const core = products.filter((p) => p.category === "CORE");
    const ps = products.filter((p) => p.category === "PREMIUM_SPECIAL");
    return { core, ps };
  }, [products]);

  return (
    <div className="apwo-shell apwo-shell--admin">
      <header className="apwo-shell-header">
        <p className="apwo-brand-eyebrow">AUTOPARTSB2B</p>
        <h1 className="apwo-shell-title">Admin — Catalogue</h1>
        <div className="apwo-shell-actions">
          <span className="apwo-user-chip">{user?.display_name} <span className="apwo-role-tag">{user?.role}</span></span>
          <button className="apwo-btn-secondary" onClick={logout} data-testid="admin-logout">Sign out</button>
        </div>
      </header>
      <main className="apwo-shell-main">
        <nav className="apwo-nav-tabs" aria-label="Admin sections">
          <a href="/admin" className="apwo-nav-tab" data-testid="admin-nav-users">Users & Overview</a>
          <a href="/admin/orders" className="apwo-nav-tab" data-testid="admin-nav-orders">Orders Inbox</a>
          <a href="/admin/catalogue" className="apwo-nav-tab active" data-testid="admin-nav-catalogue">Catalogue</a>
          <a href="/admin/backorders" className="apwo-nav-tab" data-testid="admin-nav-backorders">Backorders</a>
        </nav>

        <section className="apwo-panel">
          <div className="apwo-catalogue-toolbar">
            <h2 style={{ margin: 0 }}>Products <span className="apwo-cell-muted">({products?.length ?? "…"} total)</span></h2>
            <button className="apwo-btn-primary" onClick={openAdd} data-testid="cat-add-btn">Add product</button>
          </div>
          {banner && <div className="apwo-alert-success" role="status" data-testid="cat-banner">{banner.text}</div>}
          {error && <div className="apwo-alert-error" role="alert">{error}</div>}
          {!products && !error && <p className="apwo-muted">Loading…</p>}
          {grouped && (["core", "ps"]).map((groupKey) => {
            const rows = grouped[groupKey];
            if (!rows.length) return null;
            const isCore = groupKey === "core";
            return (
              <div key={groupKey} style={{ marginTop: "16px" }}>
                <h3 style={{ margin: "8px 0" }}>{isCore ? "Core products" : "Premium / Special products"}</h3>
                <div style={{ overflowX: "auto" }}>
                  <table className="apwo-table apwo-cat-table" data-testid={`cat-table-${groupKey}`}>
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Name</th>
                        <th>Type / Spec</th>
                        <th>Pack</th>
                        <th className="apwo-th-right">Standard</th>
                        <th className="apwo-th-right">{isCore ? "10+" : "Mixed 20+"}</th>
                        <th className="apwo-th-right">{isCore ? "48+" : "—"}</th>
                        <th className="apwo-th-center">Stock</th>
                        <th className="apwo-th-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((p) => (
                        <tr key={p.product_id} data-testid={`cat-row-${p.product_id}`}>
                          <td>{p.product_id}</td>
                          <td>{p.display_name}</td>
                          <td className="apwo-cell-muted">{p.type} · {p.specifications}</td>
                          <td>{p.pack_size}</td>
                          <td className="apwo-cat-price-cell">{CENTS(p.prices_cents?.standard)}</td>
                          <td className="apwo-cat-price-cell">
                            {isCore ? CENTS(p.prices_cents?.ten_plus) : CENTS(p.prices_cents?.mixed_twenty)}
                          </td>
                          <td className="apwo-cat-price-cell">{isCore ? CENTS(p.prices_cents?.forty_eight_plus) : "—"}</td>
                          <td className="apwo-th-center">
                            <span className={`apwo-cat-status-pill ${p.in_stock ? "apwo-cat-status-pill--in" : "apwo-cat-status-pill--out"}`}
                                  data-testid={`cat-stock-${p.product_id}`}>
                              {p.in_stock ? "In stock" : "Out of stock"}
                            </span>
                          </td>
                          <td className="apwo-th-right">
                            <div className="apwo-cat-actions">
                              <button className="apwo-btn-secondary apwo-btn-sm" onClick={() => toggle(p)} data-testid={`cat-toggle-${p.product_id}`}>
                                {p.in_stock ? "Mark OOS" : "Mark in stock"}
                              </button>
                              <button className="apwo-btn-secondary apwo-btn-sm" onClick={() => openEdit(p)} data-testid={`cat-edit-${p.product_id}`}>Edit</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </section>
      </main>
      <footer className="apwo-shell-footer">
        AUTOPARTSB2B &middot; S1 Asia Pacific Traders Pty Ltd &middot; ABN 69 650 366 091
      </footer>

      {modalMode && (
        <ProductModal
          mode={modalMode}
          initial={modalInitial}
          onClose={() => { setModalMode(null); setModalInitial(null); }}
          onDone={onModalDone}
        />
      )}
    </div>
  );
}
